import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import { routes } from 'src/client/utils/router';
import { FooterBarModal } from 'src/client/containers/FooterBar/FooterBarModal';
import { FooterCategoryMenu } from './FooterCategoryMenu';
import { OnPageFooter } from 'src/client/components/OnPageSeo/OnPageFooter';
import { FooterBarNewsletter } from './FooterBarNewsletter';
import { FooterCompliance } from './FooterCompliance';
import { ConstrainedContainer, DESKTOP, TABLET_PORTRAIT } from 'src/client/constants/mediaQueries';
import { FooterBarSocials } from './FooterBarSocials';
import { getFooterCategoryLabels } from './footerCategoryLables';
import { MobileFooterNav } from './MobileFooterNav/MobileFooterNav';
import { canViewSfwImages, getSfwTotalPages } from 'src/shared/sfwImages';
import { getSfwPagePath } from 'src/client/containers/SfwImages/paths';

const FooterSection = styled(ConstrainedContainer)`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 298px;
  padding: 0;

  @media ${TABLET_PORTRAIT} {
    flex-direction: row;
    flex-wrap: wrap;
    padding: 0 15px 0 15px;
    gap: 20px;
    min-height: auto;
  }

  @media ${DESKTOP} {
    flex-wrap: nowrap;
  }
`;

const FooterMenu = styled.ul`
  display: grid;
  grid-auto-rows: min-content;
  grid-template-columns: 1fr;
  margin: 0 auto;
  list-style: none;
  width: 100%;
  padding: 0;

  @media ${TABLET_PORTRAIT} {
    grid-column-gap: 30px;
    grid-template-columns: repeat(4, 1fr);
  }

  @media ${DESKTOP} {
    width: auto;
  }
`;

const FooterBackgroundContainer = styled.div`
  background: ${(props) => props.theme.footerBarPrimary1};
  color: ${(props) => props.theme.footerBarPrimary2};
  padding-bottom: 65px;

  @media ${TABLET_PORTRAIT} {
    padding-bottom: 0;
    padding-top: 20px;
  }
`;

export const FooterBar = () => {
  const { loaded: userLoaded, user } = useSelector((state) => state.auth);
  const siteAbbreviation = useSelector((state) => state.site.abbreviation);
  const { config, ssoUrl } = useSelector((state) => state.app);

  const [openedCategories, setOpenCategories] = useState<string[]>([]);
  const [infoModal, setInfoModal] = useState<boolean>(false);

  const {
    camsUrl,
    customerSupportUrl,
    hasActorsWanted,
    hasCommunityGuidelines,
    hasDirectoresWanted,
    hasLatestUpdates,
    hasPhilosophy,
    hasPhotographersWanted,
    hasReportAbuselink,
    privacyUrl,
    termsUrl,
    webmastersUrl,
  } = config.footer;

  const labels = getFooterCategoryLabels(siteAbbreviation);

  return (
    <>
      <OnPageFooter />
      <FooterBarModal onHide={() => setInfoModal(!infoModal)} privacyUrl={privacyUrl} show={infoModal} />
      <FooterBackgroundContainer>
        <FooterSection>
          <FooterBarNewsletter setInfoModal={setInfoModal} infoModal={infoModal} />

          <FooterMenu>
            <FooterCategoryMenu
              openedCategories={openedCategories}
              setOpenCategories={setOpenCategories}
              id="amazing"
              label="Amazing Content"
              links={[
                ...(hasLatestUpdates
                  ? [
                      {
                        label: 'Latest Updates',
                        to: '/updates',
                      },
                    ]
                  : []),
                {
                  label: labels['/erotic-films'],
                  to: '/erotic-films',
                },
                {
                  label: labels['/galleries'],
                  to: '/galleries',
                },
                {
                  label: labels['/models'],
                  to: '/models',
                },
                ...(config.galleries.botd.active
                  ? [
                      {
                        label: 'Beauty of the Day',
                        to: '/photo-archive',
                      },
                    ]
                  : []),
                {
                  label: 'Live Cams',
                  openNewWindow: true,
                  to: camsUrl,
                },
                {
                  label: 'Intimate Toys',
                  openNewWindow: true,
                  to: `https://goddess.me/?utm_medium=web&utm_source=${siteAbbreviation}&utm_campaign=footer`,
                },
                ...(config.footer.aboutUsUrl
                  ? [
                      {
                        label: 'About Us',
                        to: config.footer.aboutUsUrl,
                      },
                    ]
                  : []),
                ...(userLoaded && canViewSfwImages(user) && getSfwTotalPages(config) > 0
                  ? [{ label: 'SFW Images', to: getSfwPagePath(1) }]
                  : []),
              ]}
            />

            <FooterCategoryMenu
              openedCategories={openedCategories}
              setOpenCategories={setOpenCategories}
              id="affiliate"
              label="Become Affiliated"
              links={[
                {
                  isExternal: true,
                  label: 'Webmasters',
                  to: webmastersUrl,
                },
                ...(hasActorsWanted
                  ? [
                      {
                        className: 'standout',
                        isExternal: true,
                        label: 'Models Apply Here',
                        to: 'https://help.metart.network/artists-models/become-a-model/model-submission-guidelines',
                      },
                    ]
                  : []),
                ...(hasPhotographersWanted
                  ? [
                      {
                        isExternal: true,
                        label: 'Photographers Wanted',
                        to:
                          'https://help.metart.network/artists-models/become-a-photographer/photographer-technical-guidelines',
                      },
                    ]
                  : []),
                ...(hasDirectoresWanted
                  ? [
                      {
                        isExternal: true,
                        label: 'Directors Wanted',
                        to:
                          'https://help.metart.network/artists-models/become-a-director/director-technical-guidelines',
                      },
                    ]
                  : []),
                ...(hasPhilosophy
                  ? [
                      {
                        isExternal: true,
                        label: 'Philosophy',
                        to: 'https://help.metart.network/welcome/company-philosophy',
                      },
                    ]
                  : []),
                ...(hasCommunityGuidelines
                  ? [
                      {
                        isExternal: true,
                        label: 'Community Guidelines',
                        to: ' https://help.metart.network/policies-rules/community-guidelines',
                      },
                    ]
                  : []),
                {
                  label: 'Report Abuse',
                  to: routes.report.routes.abuse.path,
                },
              ]}
            />

            <FooterCategoryMenu
              openedCategories={openedCategories}
              setOpenCategories={setOpenCategories}
              id="service"
              label="Account & Services"
              links={[
                ...(!user
                  ? [
                      {
                        isExternal: true,
                        label: 'Member Login',
                        to: ssoUrl,
                      },
                      {
                        isExternal: true,
                        label: 'Lost Password',
                        to: `${ssoUrl}/forgot-password`,
                      },
                    ]
                  : []),
                ...(user
                  ? [
                      {
                        isExternal: true,
                        label: 'Manage Account',
                        openNewWindow: true,
                        to: ssoUrl,
                      },
                    ]
                  : []),
                {
                  isExternal: true,
                  label: 'Customer Support',
                  to: customerSupportUrl,
                },
                {
                  isExternal: true,
                  label: 'Terms & Conditions',
                  to: termsUrl,
                },
                {
                  isExternal: true,
                  label: 'Privacy',
                  to: privacyUrl,
                },
                ...(hasReportAbuselink
                  ? [
                      {
                        label: 'Report Piracy',
                        to: '/piracy',
                      },
                    ]
                  : []),
              ]}
            />

            <FooterBarSocials />
          </FooterMenu>
        </FooterSection>
        <FooterCompliance />
      </FooterBackgroundContainer>
      <MobileFooterNav />
    </>
  );
};
